import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { MAX_SYSTEM_PROMPT_CHANGELOG_LENGTH, MAX_SYSTEM_PROMPT_LENGTH } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';

export function AdminSystemPromptEdit({ promptKey }: { promptKey: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const detailQ = useQuery({
    queryKey: ['admin', 'system-prompts', promptKey],
    queryFn: () => api.adminSystemPromptGet(promptKey),
  });

  const [value, setValue] = useState('');
  const [changelog, setChangelog] = useState('');
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [initialValue, setInitialValue] = useState('');

  useEffect(() => {
    if (detailQ.data) {
      setValue(detailQ.data.currentValue);
      setInitialValue(detailQ.data.currentValue);
    }
  }, [detailQ.data]);

  const detail = detailQ.data;
  const isDirty = value !== initialValue || changelog.trim().length > 0;
  const isOverLimit = value.length > MAX_SYSTEM_PROMPT_LENGTH;
  const matchesDefault = detail ? value === detail.defaultValue : false;

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!detail) return;
      const body: { value: string; changelog?: string } = { value };
      const cl = changelog.trim();
      if (cl) body.changelog = cl;
      if (matchesDefault) {
        const restoreBody: { changelog?: string } = {};
        if (cl) restoreBody.changelog = cl;
        await api.adminSystemPromptRestoreDefault(detail.key, restoreBody);
      } else {
        await api.adminSystemPromptSave(detail.key, body);
      }
    },
    onSuccess: () => {
      setSavedMsg(matchesDefault ? '기본값으로 복원되어 저장됨' : '저장됨');
      setServerError(null);
      qc.invalidateQueries({ queryKey: ['admin', 'system-prompts'] });
      qc.invalidateQueries({ queryKey: ['admin', 'system-prompts', promptKey] });
      qc.invalidateQueries({ queryKey: ['admin', 'system-prompts', promptKey, 'versions'] });
      setTimeout(() => navigate({ to: '/admin/system-prompts' }), 600);
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : '저장에 실패했습니다.');
    },
  });

  const restoreDefault = () => {
    if (!detail) return;
    setValue(detail.defaultValue);
    setSavedMsg(null);
  };

  const charCount = value.length;
  const counterClass = cn(
    'tabular-nums',
    isOverLimit
      ? 'text-destructive font-medium'
      : charCount === MAX_SYSTEM_PROMPT_LENGTH
        ? 'text-destructive font-medium'
        : charCount >= MAX_SYSTEM_PROMPT_LENGTH * 0.9
          ? 'text-amber-600 dark:text-amber-400'
          : 'text-foreground-muted',
  );

  return (
    <PageShell
      header={
        <PageHeader
          title={detail?.label ?? '시스템 프롬프트 편집'}
          description={detail?.description}
          actions={
            <Button
              variant="secondary"
              onClick={() =>
                navigate({ to: '/admin/system-prompts/$key', params: { key: promptKey } })
              }
            >
              <ArrowLeft className="size-4" />
              취소
            </Button>
          }
        />
      }
    >
      {detailQ.isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : detail ? (
        <div className="space-y-4 max-w-3xl">
          {detail.hint ? (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
              {detail.hint}
            </div>
          ) : null}

          <div>
            <label htmlFor="sp-value" className="block text-sm font-medium mb-1.5">
              프롬프트 본문
            </label>
            <textarea
              id="sp-value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              maxLength={MAX_SYSTEM_PROMPT_LENGTH}
              rows={14}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono leading-relaxed"
              spellCheck={false}
            />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className="text-foreground-muted">
                빈 값으로 저장하면 이 가이드가 비활성화됩니다.
              </span>
              <span className={counterClass}>
                {charCount}/{MAX_SYSTEM_PROMPT_LENGTH}
              </span>
            </div>
          </div>

          <div>
            <label htmlFor="sp-changelog" className="block text-sm font-medium mb-1.5">
              변경 메모 (선택)
            </label>
            <input
              id="sp-changelog"
              type="text"
              value={changelog}
              onChange={(e) => setChangelog(e.target.value)}
              maxLength={MAX_SYSTEM_PROMPT_CHANGELOG_LENGTH}
              placeholder="이 변경을 왜 했는지 짧게 메모"
              className="w-full h-9 rounded-md border border-border bg-surface px-3 text-sm"
            />
            <div className="mt-1 text-right text-xs text-foreground-muted tabular-nums">
              {changelog.length}/{MAX_SYSTEM_PROMPT_CHANGELOG_LENGTH}
            </div>
          </div>

          {savedMsg ? (
            <div className="rounded-md border border-success/30 bg-success/5 px-3 py-2 text-sm text-success">
              {savedMsg}
            </div>
          ) : null}
          {serverError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {serverError}
            </div>
          ) : null}

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={restoreDefault}
              disabled={value === detail.defaultValue}
            >
              기본값 복원
            </Button>
            <Button
              type="button"
              onClick={() => saveMut.mutate()}
              disabled={!isDirty || isOverLimit || saveMut.isPending}
            >
              {saveMut.isPending ? '저장 중…' : '저장'}
            </Button>
          </div>
        </div>
      ) : null}
    </PageShell>
  );
}
